import type { NextPage, GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { base_domain, base_url } from '@/utils';

// TypeScript declarations for global window properties
declare global {
  interface Window {
    jQuery: any;
    Webflow: any;
  }
}

const FEATURE_ITEMS = [
  {
    name: 'Create',
    number: '1',
    description: 'Begin your story with a guided template or start from scratch.',
    image: '/webflow-home/images/tab-1-create-copy-dog.png',
    imageAlt: 'Screenshot of creating a Memvy story',
    sizes: '(max-width: 767px) 100vw, 549px',
    srcSet: '/webflow-home/images/tab-1-create-copy-p-dog-500.png 500w, /webflow-home/images/tab-1-create-copy-p-dog-800.png 800w, /webflow-home/images/tab-1-create-copy-dog.png 1098w'
  },
  {
    name: 'Collaborate',
    number: '2',
    description: 'Invite friends and family to add their memories, voices, and perspectives.',
    image: '/webflow-home/images/tab-2-collab-dog.png',
    imageAlt: 'Screenshot of collaborating on a Memvy story',
    sizes: '(max-width: 767px) 100vw, 549px',
    srcSet: '/webflow-home/images/tab-2-collab-p-dog-500.png 500w, /webflow-home/images/tab-2-collab-p-dog-800.png 800w, /webflow-home/images/tab-2-collab-dog.png 1098w'
  },
  {
    name: 'Interact',
    number: '3',
    description: 'Explore and relive your shared memories, anytime and anywhere.',
    image: '/webflow-home/images/tab-3-interact-dog.png',
    imageAlt: 'Screenshot of interacting with a Memvy story',
    sizes: '(max-width: 767px) 100vw, 549px',
    srcSet: '/webflow-home/images/tab-3-interact-p-dog-500.png 500w, /webflow-home/images/tab-3-interact-p-dog-800.png 800w, /webflow-home/images/tab-3-interact-dog.png 1098w'
  }
] as const;

const FAQ_ITEMS = [
  {
    category: 'Getting Started',
    question: 'Is there a mobile app for Memvy?',
    answer: (
      <>
        Yes, Memvy is available now on the{' '}
        <a href="https://apps.apple.com/us/app/memvy/id6757810342" className="inline-link" target="_blank" rel="noopener noreferrer">
          Apple App Store
        </a>
        {' '}and on{' '}
        <a href="https://play.google.com/store/apps/details?id=com.memvy" className="inline-link" target="_blank" rel="noopener noreferrer">
          Google Play
        </a>
        . You can also use Memvy in any mobile web browser.
      </>
    )
  },
  {
    category: 'Pricing & Storage',
    question: 'How much does Memvy cost?',
    answer: (
      <>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>Creating a Memvy account is free, and so is collaborating. Once you have an account, you can join an unlimited number of stories you&apos;re invited to and add your photos, videos, audio, and written memories to every one of them without ever paying.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>A subscription is for creating your own stories. For $1.99/mo you can create as many stories as you&apos;d like and invite as many collaborators as you&apos;d like, sharing 50 GB of storage across all of them. Your first 90 days are free, and the clock starts when you subscribe, not when you create your account. Need more room? Additional storage is available, and the storage grows faster than the price does, so the more you add, the less each gigabyte costs.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>
          There is also a single story license for $99 (one-time, 5-year term) with 50 GB included.{' '}
          <a
            href="#pricing"
            className="inline-link"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            See here for our pricing plans
          </a>
          .
        </p>
      </>
    )
  },
  {
    category: 'Stories & Privacy',
    question: 'Who can see my stories?',
    answer: (
      <>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>When you create a story, you are the only person who can see it. It stays that way until you invite someone.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>From that point the story is in DRAFT mode. You invite collaborators by sending a digital link or by having them scan a QR code, and the only people who can see a draft story are those who accepted one of those invitations. Collaborators add their memories while you and any admins you name decide what stays in the story.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>When the story is finished, you publish it as either PUBLIC or PRIVATE. A PUBLIC story can be found through Search in Memvy and opened by anyone, and anyone you send the story link to can open and view it. No account is needed to view a public story — the link opens it in any mobile browser at <a href="https://memvy.com" className="inline-link">memvy.com</a>, and the Memvy app offers a richer way to experience it. A PRIVATE story is removed from Search entirely. Only the people who were on the collaborator list when you published it can open it, whether or not they ever added a memory of their own, and their access is granted automatically the moment you publish.</p>
      </>
    )
  },
  {
    category: 'Stories & Privacy',
    question: 'Who decides what goes into my story?',
    answer: <>You do. When a collaborator uploads a memory, it goes to you and any admins you&apos;ve named for review before it appears in the story. You decide whether to allow it or deny it, and a denied memory is permanently deleted. Anything you or your admins upload posts to the story directly, since you chose those admins yourself. No ads, no algorithm, no tracking, and nothing in your story that you didn&apos;t put there on purpose.</>
  },
  {
    category: 'Stories & Privacy',
    question: 'Can I change a story after I publish it?',
    answer: <>Yes. Publishing is not a one-way door. You can move a published story back to DRAFT whenever you want. You also decide at the moment you publish whether collaborators can keep adding memories, and you can change that setting later if you change your mind.</>
  },
  {
    category: 'Stories & Privacy',
    question: 'Can I share a private story with someone after I\'ve published it?',
    answer: <>Yes. As the story owner, you or an admin can send that person the story link along with the access code. When they open the link, the code is added to their app and they have access to the story from then on. Access to a private story is always yours to grant, never automatic.</>
  },
  {
    category: 'Getting Started',
    question: 'Why do I need an account?',
    answer: (
      <>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>Your account accomplishes two things. First, when you add memories as a collaborator to anyone else&apos;s story, they can see that it comes from you.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>Second, every story you collaborate on or create (once you have a subscription) is connected to your account. This means that whenever you log in, you will always see the full list of stories you are connected to.</p>
      </>
    )
  },
  {
    category: 'Getting Started',
    question: 'How do I invite people to join my story?',
    answer: <>You can send a digital invitation link to anyone via any digital means (e.g., email, text). You can also share the invitation in the form of a QR code on printed materials, social media post, etc. Pull the invitation up in the Memvy app on your phone and the QR code on your screen is live for that story, so anyone standing with you can scan it and join on the spot.</>
  },
  {
    category: 'Pricing & Storage',
    question: 'What types of media can I upload?',
    answer: <>You can upload audio recordings, images, videos, and text relevant to the story. However, we cannot accept any copyrighted material. You are also prohibited from uploading any offensive material, such as adult content or hate speech. Any copyrighted or offensive material will be subject to removal and your account may be permanently banned.</>
  },
  {
    category: 'Pricing & Storage',
    question: 'Is there a limit to how much data I can have on my account?',
    answer: <>Depending on your subscription level, you have a limit to the total amount of data across all of your stories. The total number of stories is unlimited, but the total data is not. Regular Consumer Subscriptions start at 50GB. Corporate subscriptions have a much higher level and different price point — see below for how Corporate Accounts work.</>
  },
  {
    category: 'Organizations',
    question: 'What is a Corporate Account?',
    answer: (
      <>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>Corporate Accounts are for organizations — nonprofits, schools, clubs, associations, and businesses — that want to capture their shared history in one place the organization owns. Storage starts at 1TB and scales from there.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>Every Corporate Account includes personalized training on account setup, story creation, and administrator features. Corporate Accounts also have Permanent Admins, a role not available on consumer accounts: designated people who can create and administer stories across the whole account, so your storytelling isn&apos;t tied to a single person&apos;s login. Collaborators still participate for free, however many you have.</p>
        <p className="caption dark" style={{ fontWeight: 'inherit', lineHeight: 'inherit' }}>Corporate Accounts can be paid monthly, annually, or every three years. Memvy also makes in-kind accounts available to recognized nonprofit organizations in good standing, at the sole discretion of Memvy leadership. To get started, email <a href="mailto:sales@memvy.com" className="inline-link">sales@memvy.com</a>.</p>
      </>
    )
  },
  {
    category: 'Help & Policies',
    question: 'How can I contact support?',
    answer: <>You can contact support by emailing <a href="mailto:support@memvy.com" className="inline-link">support@memvy.com</a>.</>
  },
  {
    category: 'Help & Policies',
    question: 'Can I provide feedback or suggest new features?',
    answer: <>We welcome your feedback and suggestions. You can provide feedback by contacting us at <a href="mailto:support@memvy.com" className="inline-link">support@memvy.com</a>.</>
  },
  {
    category: 'Help & Policies',
    question: 'Do you have any press/media guidelines?',
    answer: <>Yes, you can contact us at <a href="mailto:media@memvy.com" className="inline-link">media@memvy.com</a> for any branding or media-related requests.</>
  }
] as const;

const FAQ_CATEGORIES = ['All', 'Getting Started', 'Stories & Privacy', 'Pricing & Storage', 'Organizations', 'Help & Policies'] as const;
type FaqCategory = typeof FAQ_CATEGORIES[number];

type FeatureName = typeof FEATURE_ITEMS[number]['name'];

const LandingPage: NextPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeMobileFeature, setActiveMobileFeature] = useState<FeatureName | null>('Create');
  const [activeFaqCategory, setActiveFaqCategory] = useState<FaqCategory>('All');
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    console.log('Starting Webflow initialization process...');

    // Fade in as soon as CSS is loaded and basic content is ready
    const showPageEarly = () => {
      console.log('Hero and video sections ready - fading in page');
      setIsLoaded(true);
    };

    // Set a quick timeout to show the page once CSS is loaded
    const quickShowTimer = setTimeout(showPageEarly, 300); // Very quick - just wait for CSS

    // Load Webflow CSS dynamically for this page only
    const loadCSS = (href: string) => {
      // Check if CSS is already loaded
      if (document.querySelector(`link[href="${href}"]`)) return null;

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = href;
      link.setAttribute('data-webflow-css', 'true');
      document.head.appendChild(link);
      return link;
    };

    const buildId = window.__NEXT_DATA__?.buildId || 'development';
    const cssFiles = [
      '/webflow-home/css/normalize.css',
      '/webflow-home/css/webflow.css',
      '/webflow-home/css/memvy.webflow.css'
    ].map(href => `${href}?v=${encodeURIComponent(buildId)}`);

    const loadedLinks = cssFiles.map(loadCSS).filter(Boolean);

    // Load and initialize Webflow
    const initializeWebflow = () => {
      console.log('Checking for jQuery...');
      // Check if jQuery is already loaded
      if (!window.jQuery) {
        console.log('Loading jQuery...');
        const jqueryScript = document.createElement('script');
        jqueryScript.src = 'https://d3e54v103j8qbb.cloudfront.net/js/jquery-3.5.1.min.dc5e7f18c8.js?site=680bad4873ef03f08322616c';
        jqueryScript.setAttribute('data-webflow-js', 'true');

        jqueryScript.onload = () => {
          console.log('jQuery loaded successfully');
          loadWebflowScript();
        };

        jqueryScript.onerror = () => {
          console.error('Failed to load jQuery');
        };

        document.head.appendChild(jqueryScript);
      } else {
        console.log('jQuery already available');
        loadWebflowScript();
      }
    };

    const loadWebflowScript = () => {
      console.log('Loading Webflow script...');

      // Remove any existing Webflow script first
      const existingScript = document.querySelector('script[src="/webflow-home/js/webflow.js"]');
      if (existingScript) {
        console.log('Removing existing Webflow script');
        existingScript.remove();
      }

      const webflowScript = document.createElement('script');
      webflowScript.src = '/webflow-home/js/webflow.js';
      webflowScript.setAttribute('data-webflow-js', 'true');

      webflowScript.onload = () => {
        console.log('Webflow script loaded successfully');
        setTimeout(() => {
          console.log('=== DEBUGGING WEBFLOW ===');
          console.log('Webflow object:', window.Webflow);
          console.log('jQuery available:', !!window.jQuery);

          // Let's see what interactive elements we actually have
          const sliders = document.querySelectorAll('[data-w-slider]');
          const tabs = document.querySelectorAll('[data-w-tab]');
          const navs = document.querySelectorAll('[data-w-nav]');
          const dropdowns = document.querySelectorAll('[data-w-dropdown]');
          const faqAccordions = document.querySelectorAll('.faq-accordion.w-dropdown');

          console.log('=== FOUND ELEMENTS ===');
          console.log('Sliders:', sliders.length, sliders);
          console.log('Tabs:', tabs.length, tabs);
          console.log('Navs:', navs.length, navs);
          console.log('Dropdowns:', dropdowns.length, dropdowns);
          console.log('FAQ Accordions:', faqAccordions.length, faqAccordions);

          // Use the FULL Webflow initialization that we know works
          if (window.Webflow) {
            try {
              console.log('Trying FULL Webflow initialization...');
              window.Webflow.destroy();
              window.Webflow.ready();
              window.Webflow.require('ix2').init();
              console.log('FULL Webflow initialization successful - interactions should work now!');
            } catch (error) {
              console.error('Error with full Webflow initialization:', error);
            }
          } else {
            console.error('Webflow object not available!');
          }

          // Add manual functionality for remaining interactions
          setTimeout(() => {
            console.log('Adding manual interaction handlers...');

            // Add smooth scrolling for anchor links
            const navLinks = document.querySelectorAll('.nav-link[href^="#"]');
            console.log('Found nav links for smooth scrolling:', navLinks.length);

            navLinks.forEach((link, index) => {
              console.log(`Adding listener to link ${index}:`, link.textContent, link.getAttribute('href'));

              link.addEventListener('click', (e) => {
                const targetId = link.getAttribute('href');
                console.log('Nav link clicked:', link.textContent, targetId);
                e.preventDefault();
                e.stopPropagation();

                if (!targetId) {
                  console.error('No href attribute found on link');
                  return;
                }

                const targetElement = document.querySelector(targetId);

                console.log('Target element found:', !!targetElement, targetElement);

                if (targetElement) {
                  console.log('Smooth scrolling to:', targetId);
                  targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                } else {
                  console.error('Target element not found for:', targetId);
                }
              });
            });

            // Add video modal functionality
            const modal = document.querySelector('.video-modal') as HTMLElement;
            const playBtn = document.querySelector('.play-btn') as HTMLElement;
            const closeBtn = document.querySelector('.close-div') as HTMLElement;

            console.log('Video modal elements check:');
            console.log('Modal found:', !!modal);
            console.log('Play button found:', !!playBtn);
            console.log('Close button found:', !!closeBtn);

            if (playBtn && modal) {
              console.log('Adding click listener to play button');
              playBtn.addEventListener('click', (e) => {
                console.log('Play button clicked!');
                e.preventDefault();
                e.stopPropagation();
                console.log('Opening video modal');
                modal.style.opacity = '1';
                modal.style.display = 'block';
                modal.style.zIndex = '9999';
                document.body.style.overflow = 'hidden'; // Prevent scroll
              });

              // Also try adding to the image inside the play button
              const playBtnImg = playBtn.querySelector('img');
              if (playBtnImg) {
                console.log('Adding click listener to play button image');
                playBtnImg.addEventListener('click', (e) => {
                  console.log('Play button image clicked!');
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Opening video modal via image click');
                  modal.style.opacity = '1';
                  modal.style.display = 'block';
                  modal.style.zIndex = '9999';
                  document.body.style.overflow = 'hidden';
                });
              }
            } else {
              console.error('Play button or modal not found!');
            }

            if (closeBtn && modal) {
              closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                console.log('Closing video modal');
                modal.style.opacity = '0';
                modal.style.display = 'none';
                document.body.style.overflow = ''; // Restore scroll
              });
            }

            // Close modal when clicking outside
            if (modal) {
              modal.addEventListener('click', (e) => {
                if (e.target === modal || (e.target as Element).classList.contains('video-modal-bg')) {
                  console.log('Closing video modal (clicked outside)');
                  modal.style.opacity = '0';
                  modal.style.display = 'none';
                  document.body.style.overflow = '';
                }
              });
            }

            console.log('Manual interaction handlers added successfully!');

          }, 500); // Small delay after Webflow initialization
        }, 1000);
      };

      webflowScript.onerror = () => {
        console.error('Failed to load Webflow script from /webflow-home/js/webflow.js');
      };

      document.body.appendChild(webflowScript);
      console.log('Webflow script element added to DOM');
    };

    // Initialize after a short delay to ensure DOM is fully ready
    const timer = setTimeout(initializeWebflow, 300);

    return () => {
      clearTimeout(quickShowTimer);
      clearTimeout(timer);
      console.log('Cleaning up Webflow...');

      // Cleanup Webflow
      if (window.Webflow) {
        try {
          window.Webflow.destroy();
        } catch (error) {
          console.error('Webflow cleanup error:', error);
        }
      }

      // Remove CSS files
      loadedLinks.forEach(link => {
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });

      // Remove JS files
      const webflowScripts = document.querySelectorAll('[data-webflow-js="true"]');
      webflowScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);


  return (
    <>
      <Head>
        <title>Memvy — The Future of Enjoying the Past | Collaborative Stories for Families, Friends & Communities</title>
        <meta charSet="utf-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta
          name="description"
          content="The future of enjoying the past. Memvy is a private, collaborative platform for preserving and sharing photo, video, audio, and written stories with family, friends, and communities."
        />

        {/* CSS will be loaded dynamically in useEffect */}

        {/* Google Fonts */}
        <link href="https://fonts.googleapis.com" rel="preconnect" />
        <link href="https://fonts.gstatic.com" rel="preconnect" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;1,300;1,400&display=swap" rel="stylesheet" />

        {/* Webflow Scripts */}
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `!function(o,c){var n=c.documentElement,t=" w-mod-";n.className+=t+"js",("ontouchstart"in o||o.DocumentTouch&&c instanceof DocumentTouch)&&(n.className+=t+"touch")}(window,document);`
          }}
        />

        {/* Favicons */}
        <link href="/webflow-home/images/favicon.png" rel="shortcut icon" type="image/x-icon" />
        <link href="/webflow-home/images/webclip.png" rel="apple-touch-icon" />

        {/* Open Graph */}
        <meta property="og:url" content={`${base_url}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Memvy" />
        <meta
          property="og:description"
          content="The future of enjoying the past. Create richer and deeper stories of your shared experiences by sharing audio, image, and video memories to collaborate with friends and family to share your cherished memories."
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={base_domain} />
        <meta property="twitter:url" content={`${base_url}`} />
        <meta name="twitter:title" content="Memvy" />
        <meta
          name="twitter:description"
          content="The future of enjoying the past. Create richer and deeper stories of your shared experiences by sharing audio, image, and video memories to collaborate with friends and family to share your cherished memories."
        />

        {/* Custom Styles */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
              html {
                -webkit-font-smoothing: antialiased;
              }

              /* Ensure smooth loading */
              body {
                overflow-x: hidden;
              }
            `
          }}
        />
      </Head>

      <div
        className={`page-content ${isLoaded ? 'loaded' : ''}`}
        style={{
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.6s ease-in-out'
        }}
      >
        <div className="body">
          {/* Video Modal */}
          <div style={{ opacity: 0, display: 'none' }} className="video-modal">
            <div className="video-modal-bg">
              <a data-w-id="a8d4917b-eb6a-3df5-5374-36b4bf04a070" href="#" className="close-div w-inline-block">
                <img src="/webflow-home/images/x.svg" loading="lazy" width="30" height="auto" alt="Close video" />
              </a>
              <div className="video-div">
                <div style={{ paddingTop: '56.27659574468085%' }} className="video-vimeo w-video w-embed">
                  <iframe
                    className="embedly-embed"
                    src="https://cdn.embedly.com/widgets/media.html?src=https%3A%2F%2Fplayer.vimeo.com%2Fvideo%2F1019539253%3Fh%3D4eb7d302cd%26app_id%3D122963&dntp=1&display_name=Vimeo&url=https%3A%2F%2Fvimeo.com%2F1019539253%2F4eb7d302cd&image=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F1937935118-089f77b57b60710103be650d92b51f1a4fc20f1813cd9602a57c0af24195426d-d_1280&type=text%2Fhtml&schema=vimeo"
                    width="940"
                    height="529"
                    scrolling="no"
                    allowFullScreen={true}
                    title="Meet Memvy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div data-animation="default" data-collapse="medium" data-duration="400" data-easing="ease" data-easing2="ease" role="banner" className="nav w-nav">
            <div className="menu-button w-nav-button">
              <div className="menu-button-icon w-icon-nav-menu"></div>
            </div>
            <a href="#top" className="brand w-nav-brand">
              <img src="/webflow-home/images/Logo_Nav.svg" loading="lazy" alt="Memvy Logo" />
            </a>
            <nav role="navigation" className="nav-menu w-nav-menu">
              <a href="#features" className="nav-link w-nav-link">Features</a>
              <a href="#why-memvy" className="nav-link w-nav-link">Why Memvy</a>
              <a href="#compare" className="nav-link w-nav-link">Compare</a>
              <a href="#news" className="nav-link w-nav-link">News</a>
              <a href="#partners" className="nav-link w-nav-link">Partners</a>
              <a href="#organizations" className="nav-link w-nav-link">Organizations</a>
              <a href="#FAQ" className="nav-link w-nav-link">FAQ</a>
              <a href="#pricing" className="nav-link w-nav-link">Pricing</a>
            </nav>
            <Link href="/app/register" className="btn w-nav-link">
            Log In
            </Link>
          </div>

          {/* Hero Section */}
          <section id="top" className="hero-section">
            <div className="header-container">
              <h1 className="heading">The Future of Enjoying the Past</h1>
              <div className="y-padding-12">
                <p className="paragraph-2">Seamlessly share memories with the people you love to capture the moments you cherish.</p>
                <div className="y-padding-12">
                  <Link href="/app/register" target="_blank" className="btn w-button">
                    Start Making Memories
                  </Link>
                </div>
              </div>
            </div>
            <div
              data-w-id="e56e8b84-597d-d9f6-77d5-cf1c98413409"
              style={{
                WebkitTransform: 'translate3d(0, 0, 0) scale3d(1, 1, 1) rotateX(0) rotateY(0) rotateZ(0) skew(0, 0)',
                MozTransform: 'translate3d(0, 0, 0) scale3d(1, 1, 1) rotateX(0) rotateY(0) rotateZ(0) skew(0, 0)',
                msTransform: 'translate3d(0, 0, 0) scale3d(1, 1, 1) rotateX(0) rotateY(0) rotateZ(0) skew(0, 0)',
                transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1) rotateX(0) rotateY(0) rotateZ(0) skew(0, 0)',
                transformStyle: 'preserve-3d'
              }}
              className="video-section"
            >
              <div className="div-block">
                <img
                  src="/webflow-home/images/video_thumb.png"
                  loading="lazy"
                  width="1002.5"
                  sizes="100vw"
                  alt="Meet Memvy video thumbnail"
                  srcSet="/webflow-home/images/video_thumb-p-500.png 500w, /webflow-home/images/video_thumb-p-800.png 800w, /webflow-home/images/video_thumb-p-1080.png 1080w, /webflow-home/images/video_thumb-p-1600.png 1600w, /webflow-home/images/video_thumb-p-2000.png 2000w, /webflow-home/images/video_thumb.png 2005w"
                  className="video-thumb"
                />
                <button
                  type="button"
                  data-w-id="8b57b23c-441f-2066-1fe9-62bc82ff7beb"
                  className="play-btn"
                  aria-label="Play Memvy video"
                >
                  <img src="/webflow-home/images/play.svg" loading="lazy" width="28" alt="" aria-hidden="true" />
                  <span className="play-btn-label">Play video</span>
                </button>
              </div>
              <div className="video-caption">
                <span className="video-caption-meta">1 min watch</span>
                <h3 className="video-caption-title">See how Memvy works</h3>
                <p className="video-caption-copy">
                  Discover how families and communities can preserve photos, videos, audio, and stories together—all in
                  one place.
                </p>
              </div>
              <div style={{ paddingTop: '56.27659574468085%' }} className="video w-video w-embed">
                <iframe
                  className="embedly-embed"
                  src="https://cdn.embedly.com/widgets/media.html?src=https%3A%2F%2Fplayer.vimeo.com%2Fvideo%2F1019539253%3Fh%3D4eb7d302cd%26app_id%3D122963&dntp=1&display_name=Vimeo&url=https%3A%2F%2Fvimeo.com%2F1019539253%2F4eb7d302cd&image=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F1937935118-089f77b57b60710103be650d92b51f1a4fc20f1813cd9602a57c0af24195426d-d_1280&type=text%2Fhtml&schema=vimeo"
                  width="940"
                  height="529"
                  scrolling="no"
                  allowFullScreen={true}
                  title="Memvy"
                />
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="transparent-section down-thread">
            <div className="descriptor-title">Features</div>
            <div className="y-padding-12"></div>
            <div className="slider-flex">
              <h2 className="h2">Relive</h2>
              <div data-delay="0" data-animation="outin" className="text-slider w-slider" data-autoplay="true" data-easing="ease" data-hide-arrows="true" data-disable-swipe="false" data-autoplay-limit="0" data-nav-spacing="3" data-duration="1000" data-infinite="true">
                <div className="text-slider-mask w-slider-mask">
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">your wedding</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">Grandma&apos;s legacy</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">the playoffs</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">that epic trip</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">Dad&apos;s retirement</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">our family reunion</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">big milestones</span><br /></h2>
                  </div>
                  <div className="w-slide">
                    <h2 className="h2"><span className="slide-header">your memories</span><br /></h2>
                  </div>
                </div>
                <div className="left-arrow w-slider-arrow-left">
                  <div className="icon w-icon-slider-left"></div>
                </div>
                <div className="right-arrow w-slider-arrow-right"></div>
                <div className="hide w-slider-nav w-round w-num"></div>
              </div>
            </div>
            <h2 className="h2">for years to come. </h2>

            {/* Features Tabs */}
            <div className="content-container _1024">
              <div data-current="Create" data-easing="ease" data-duration-in="300" data-duration-out="100" className="features-tabs desktop-feature-tabs w-tabs">
                <div className="features-tabs-menu w-tab-menu">
                  {FEATURE_ITEMS.map((feature, index) => (
                    <a
                      data-w-tab={feature.name}
                      className={`features-tab w-inline-block w-tab-link${index === 0 ? ' w--current' : ''}`}
                      key={feature.name}
                    >
                      <div className="tab-number-flex">
                        <div className="number-highlight">{feature.number}</div>
                        <div className="tab-heading">{feature.name}</div>
                      </div>
                      <p className="paragraph light">{feature.description}</p>
                    </a>
                  ))}
                </div>
                <div className="w-tab-content">
                  {FEATURE_ITEMS.map((feature, index) => (
                    <div
                      data-w-tab={feature.name}
                      className={`w-tab-pane${index === 0 ? ' w--tab-active' : ''}`}
                      key={feature.name}
                    >
                      <img
                        width="549"
                        sizes={'sizes' in feature ? feature.sizes : undefined}
                        alt={feature.imageAlt}
                        src={feature.image}
                        loading="lazy"
                        srcSet={'srcSet' in feature ? feature.srcSet : undefined}
                        className={feature.name === 'Create' ? 'feature-image-create' : undefined}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mobile-features-accordion">
                {FEATURE_ITEMS.map(feature => {
                  const isActive = activeMobileFeature === feature.name;
                  const featureId = `mobile-feature-${feature.name.toLowerCase()}`;

                  return (
                    <div
                      id={`${featureId}-item`}
                      className={`mobile-feature-item${isActive ? ' is-active' : ''}`}
                      key={feature.name}
                    >
                      <button
                        type="button"
                        className="mobile-feature-trigger"
                        aria-expanded={isActive}
                        aria-controls={featureId}
                        onClick={() => {
                          if (isActive) {
                            setActiveMobileFeature(null);
                            return;
                          }

                          setActiveMobileFeature(feature.name);
                          window.setTimeout(() => {
                            document.getElementById(`${featureId}-item`)?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start'
                            });
                          }, 320);
                        }}
                      >
                        <span className="tab-number-flex">
                          <span className="number-highlight">{feature.number}</span>
                          <span className="tab-heading">{feature.name}</span>
                        </span>
                        <span className="mobile-feature-chevron" aria-hidden="true"></span>
                      </button>
                      <div
                        id={featureId}
                        className="mobile-feature-panel"
                        aria-hidden={!isActive}
                      >
                        <div className="mobile-feature-panel-inner">
                          <p>{feature.description}</p>
                          <img
                            src={feature.image}
                            srcSet={feature.srcSet}
                            sizes={feature.sizes}
                            loading="lazy"
                            alt={feature.imageAlt}
                            className={
                              feature.name === 'Create'
                                ? 'feature-image-create'
                                : feature.name === 'Collaborate'
                                  ? 'mobile-feature-image-collaborate'
                                  : undefined
                            }
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Example Section */}
            <div className="content-container flex">
              <div className="y-padding-12 left-align">
                <h3>Want to see Memvy in action?</h3>
                <a href="https://www.memvy.com/app/story/Jordyn---Wyatt" target="_blank" className="btn w-button">See an example Memvy</a>
              </div>
              <img src="/webflow-home/images/phone_1.avif" loading="lazy" width="435.5" sizes="(max-width: 479px) 100vw, 435.5px" alt="Memvy app displayed on a mobile phone" srcSet="/webflow-home/images/phone_1phone.avif 500w, /webflow-home/images/phone_1.avif 871w" />
            </div>
          </section>

          {/* Why Memvy */}
          <div id="why-memvy" className="transparent-section">
            <div className="descriptor-title">Why Memvy</div>
            <div className="content-container _800px">
              <h2 className="h2">
                Built for <span className="header-gradient">your memories</span>, not advertisers.
              </h2>
              <p className="body-large">
                Memvy exists to help you preserve and share life&apos;s moments—without ads,
                algorithms, or distractions getting in the way. Your stories belong to you,
                and you decide how they&apos;re shared.
              </p>
            </div>

            <div className="content-container">
              <div className="w-layout-grid features-grid">
                <div className="feature-card">
                  <h3>No ads, ever</h3>
                  <p>
                    Relive your memories without sponsored content, pop-ups, or anything
                    competing for your attention.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>No tracking</h3>
                  <p>
                    We don&apos;t follow you around the web or build profiles to target you
                    with advertising.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>No data selling</h3>
                  <p>
                    Your personal information stays yours. We never sell it to third parties.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>You&apos;re in control</h3>
                  <p>
                    Choose who can view, contribute to, and preserve every story you create.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Compare Section */}
          <div id="compare" className="compare-div">
            <div className="descriptor-title">compare</div>
            <div className="content-container _800px">
              <h2 className="h2">
                <span className="header-gradient">Your best choice</span> for sharing &amp; engaging with memories.
              </h2>
            </div>
            <div className="content-container">
              <div className="w-layout-grid compare-grid">
                <div className="top-row left">
                  <div className="grid-text">Features</div>
                </div>
                <div className="top-row">
                  <img src="/webflow-home/images/memvylogo-table.svg" loading="lazy" width="97" alt="Memvy" className="image" />
                  <div className="grid-text _0-opacity">Memvy</div>
                </div>
                <div className="top-row">
                  <img src="/webflow-home/images/shared.avif" loading="lazy" width="35" alt="Shared Albums" />
                  <div className="grid-text">Shared Albums</div>
                </div>
                <div className="top-row">
                  <img src="/webflow-home/images/social.avif" loading="lazy" width="58" alt="Social Media" />
                  <div className="grid-text">Social Media</div>
                </div>
                <div className="top-row">
                  <img src="/webflow-home/images/waldo.avif" loading="lazy" width="35" alt="Waldo" />
                  <div className="grid-text">Waldo</div>
                </div>
                <div className="top-row right">
                  <img src="/webflow-home/images/storyworth.avif" loading="lazy" width="35" alt="Storyworth" />
                  <div className="grid-text">Storyworth</div>
                </div>

                {/* Row 1: Share photo & video memories */}
                <div className="grid-cell-feature text">
                  <div className="grid-text">Share photo &amp; video memories</div>
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-memvy.svg" loading="lazy" width="24" height="Auto" alt="Check Mark" className="memvy-check" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>

                {/* Row 2: Share audio & text memories */}
                <div className="grid-cell-feature text">
                  <div className="grid-text">Share audio &amp; text memories</div>
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-memvy.svg" loading="lazy" width="24" height="Auto" alt="Check Mark" className="memvy-check" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>

                {/* Row 3: Customize collaborator access */}
                <div className="grid-cell-feature text">
                  <div className="grid-text">Customize collaborator access</div>
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-memvy.svg" loading="lazy" width="24" height="Auto" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>

                {/* Row 4: Templates designed for specific events */}
                <div className="grid-cell-feature text">
                  <div className="grid-text">Templates designed for specific events</div>
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-memvy.svg" loading="lazy" width="24" height="Auto" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>

                {/* Row 5: Access to exclusive partnerships */}
                <div className="grid-cell-feature text">
                  <div className="grid-text">Access to exclusive partnerships</div>
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-memvy.svg" loading="lazy" width="24" height="Auto" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>

                {/* Row 6: Storytelling focused experience */}
                <div className="grid-cell-feature text">
                  <div className="grid-text">Storytelling focused experience</div>
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-memvy.svg" loading="lazy" width="24" height="Auto" alt="Check Mark" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/x.svg" loading="lazy" alt="Check Mark" className="x-hide" />
                </div>
                <div className="grid-cell-feature">
                  <img src="/webflow-home/images/check-dark.svg" loading="lazy" alt="Check Mark" />
                </div>
              </div>
            </div>
          </div>

          {/* News Section */}
          <div id="news" className="transparent-section">
            <div className="descriptor-title">News</div>
            <div className="content-container _800px">
              <h2 className="h2"><span className="header-gradient">Memvy</span> in the news.</h2>
            </div>
            <div className="content-container">
              <div className="w-layout-grid news-grid single">
                {/* <a href="https://roughdraftatlanta.com/2025/06/22/memvy-redefines-social-media/" target="_blank" id="w-node-_8a8a53ae-47d3-5cd5-f8db-dd9303615dee-83226183" className="news-card">
                  <h3 className="article-title">Where memories become stories that last</h3>
                  <div className="news-footer w-inline-block">
                    <span className="outlet">Rough Draft Atlanta</span>
                    <img src="/webflow-home/images/arrow-go.svg" loading="lazy" alt="Arrow" className="media-arrow" />
                  </div>
                </a> */}
                <a href="/docs/VGA-Memvy-Press-Release.pdf" target="_blank" id="w-node-_8a8a53ae-47d3-5cd5-f8db-dd9303615dee-83226183" className="news-card">
                  <h3 className="article-title">VGA Armed Forces Cup and MEMVY® Launch Groundbreaking Digital Partnership to Bring Tournament Memories to Life</h3>
                  <div className="news-footer w-inline-block">
                    {/* <span className="outlet">Rough Draft Atlanta</span> */}
                    <img src="/webflow-home/images/arrow-go.svg" loading="lazy" alt="Arrow" className="media-arrow" />
                  </div>
                </a>
                {/* <div id="w-node-e518fc70-031f-0464-65b0-6cf234280127-83226183" className="news-card blue">
                  <div className="article-title blue">This One Right Here is a Much Longer Article Title About Memvy.</div>
                  <a href="#" className="news-footer w-inline-block">
                    <img src="/webflow-home/images/fake-media-1.svg" loading="lazy" alt="Arrow" />
                    <img src="/webflow-home/images/arrow-go.svg" loading="lazy" alt="Arrow" className="media-arrow" />
                  </a>
                </div>
                <div id="w-node-_457218e9-3409-0028-a4dc-105833f2839d-83226183" className="news-card purple">
                  <div className="article-title purple">Very Short Title</div>
                  <a href="#" className="news-footer w-inline-block">
                    <img src="/webflow-home/images/fake-media-1.svg" loading="lazy" alt="Arrow" />
                    <img src="/webflow-home/images/arrow-go.svg" loading="lazy" alt="Arrow" className="media-arrow" />
                  </a>
                </div> */}
              </div>
            </div>
          </div>

          {/* Partners Section */}
          <section id="partners" className="partners-section">
            <div className="descriptor-title partners-descriptor">Partners</div>
            <div className="content-container flex">
              <div className="y-padding-12">
                <div id="w-node-_78c33615-369d-a3e0-8af7-117361707228-83226183" className="partnerships quickstack-partners wf-layout-layout">
                  <div className="partnerships__intro w-layout-cell">
                    <h3 className="h3">Partnering with organizations that share our values of connection &amp; collaboration.</h3>
                  </div>
                  <div className="partnerships__logos">
                  <a target="_blank" href="https://www.augustasoccer.com" className="partnerships__logo w-layout-cell cell-centered">
                    <img src="/webflow-home/images/nonprofit-2.avif" loading="lazy" width="110" alt="Augusta Soccer Logo" />
                  </a>
                  <a target="_blank" href="https://vgagolf.org/" className="partnerships__logo w-layout-cell cell-centered">
                    <img src="/webflow-home/images/nonprofit-1.avif" loading="lazy" width="110" alt="VGA Golf Logo" />
                  </a>
                  <a target="_blank" href="https://paceline.org/" className="partnerships__logo w-layout-cell cell-centered">
                    <img src="/webflow-home/images/paceline.png" loading="lazy" width="110" alt="Paceline Logo" />
                  </a>
                  <a target="_blank" href="https://augustahabitat.org/" className="partnerships__logo w-layout-cell cell-centered">
                    <img src="/webflow-home/images/augusta-hfh.png" loading="lazy" width="110" alt="Augusta Habitat Logo" />
                  </a>
                  <a target="_blank" href="https://www.usafa.org/" className="partnerships__logo w-layout-cell cell-centered">
                    <img src="/webflow-home/images/afa.png" loading="lazy" width="110" alt="USAFA Logo" />
                  </a>
                  <a target="_blank" href="https://www.trinityonthehill.net" className="partnerships__logo w-layout-cell cell-centered">
                    <img src="/webflow-home/images/trinity.png" loading="lazy" width="110" alt="Trinity on the Hill Logo" />
                  </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Organizations Section */}
          <section id="organizations" className="organizations-section">
            <div className="descriptor-title centered-descriptor organizations-descriptor">Organizations</div>
            <div className="content-container">
              <div className="organizations-header">
                <h2 className="h2 organizations-heading"><span className="header-gradient">Memvy</span> for Organizations</h2>
                <p className="organizations-intro-copy">Your organization&apos;s history lives on hundreds of phones. Memvy brings it into one place your organization owns.</p>
              </div>

              <div className="organizations-content">
                <div className="organizations-story">
                  <p>Most organizations have the same problem: they need content, and producing it falls to one overworked person with a camera. Memvy turns that around. Your members, guests, and volunteers contribute the memories — from the tailgate, the banquet, the job site, the sideline — and every one of them lands in a story your organization owns outright. <strong>The people who were there do the capturing. You do the curating.</strong></p>
                </div>

                <div className="organizations-account-copy">
                  <p><strong>Corporate Accounts start at 1TB and scale from there</strong>, with storage sized to an organization rather than an individual. Every account includes <strong>personalized training</strong> on setup, story creation, and administration, so your team is running before your first event.</p>
                  <p>Corporate Accounts also unlock <strong>Permanent Admins</strong> — designated people who can create and manage stories across the account, so your storytelling never depends on one person&apos;s login. And your members and guests <strong>collaborate for free</strong>, however many of them there are.</p>
                </div>
              </div>

              <div className="organizations-footer">
                <p>No ads. No tracking. No data selling. Your organization&apos;s memories belong to your organization.</p>
                <div className="organizations-contact">
                  <a href="mailto:sales@memvy.com" className="btn w-button">Ask about Corporate Accounts</a>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="FAQ" className="transparent-section">
            <div className="descriptor-title centered-descriptor faq-descriptor">FAQ</div>
            <div className="content-container">
              <div id="w-node-_8084edac-5db4-a5a0-9432-e214a8012629-83226183" className="w-layout-layout quick-stack wf-layout-layout">
                <div className="w-layout-cell cell-4">
                  <div className="left-align">
                    <h2 className="h2"><span className="header-gradient">You might be wondering</span> about Memvy.</h2>
                  </div>
                </div>
                <div className="w-layout-cell cell-3">
                  <div className="faq-filters" role="group" aria-label="Filter frequently asked questions by topic">
                    {FAQ_CATEGORIES.map((category) => (
                      <button
                        key={category}
                        type="button"
                        className={`faq-filter${activeFaqCategory === category ? ' is-active' : ''}`}
                        aria-pressed={activeFaqCategory === category}
                        onClick={(event) => {
                          setActiveFaqCategory(category);
                          setOpenFaq(null);
                          const filterRow = event.currentTarget.parentElement;
                          const centeredPosition = event.currentTarget.offsetLeft
                            - ((filterRow?.clientWidth ?? 0) - event.currentTarget.offsetWidth) / 2;

                          filterRow?.scrollTo({
                            behavior: 'smooth',
                            left: centeredPosition
                          });
                        }}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  <div className="faq-wrapper">
                    {FAQ_ITEMS
                      .filter((item) => activeFaqCategory === 'All' || item.category === activeFaqCategory)
                      .map((item) => {
                        const isOpen = openFaq === item.question;
                        const answerId = `faq-answer-${FAQ_ITEMS.indexOf(item)}`;

                        return (
                      <div key={item.question} className={`faq-accordion${isOpen ? ' is-open' : ''}`}>
                        <button
                          type="button"
                          className="accordion-toggle"
                          aria-expanded={isOpen}
                          aria-controls={answerId}
                          onClick={() => setOpenFaq(isOpen ? null : item.question)}
                        >
                          <span className="accordion-question">{item.question}</span>
                          <span className="accordion-icon" aria-hidden="true"></span>
                        </button>
                        <div id={answerId} className="accordion-answer-wrapper" aria-hidden={!isOpen}>
                          <div className="caption dark">{item.answer}</div>
                        </div>
                      </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="pricing" className="pricing" style={{ scrollMarginTop: '80px' }}>
            <div className="descriptor-title">Pricing</div>
            <div className="y-padding-12"></div>
            <h2 className="h2 white"><span className="header-gradient">Pricing</span> that works for you.</h2>
            <p style={{ textAlign: 'center', marginTop: '0', marginBottom: '0.5rem', fontSize: '1.1rem', color: '#fff', fontFamily: 'DM Sans, sans-serif' }}>Your Memvy account is always free. Create and collaborate with friends at no cost.</p>
            <p style={{ textAlign: 'center', marginTop: '0', marginBottom: '1rem', fontSize: '1.1rem', color: '#fff', fontFamily: 'DM Sans, sans-serif' }}>Ready for more? Upgrade to a subscription — <strong>free for 90 days</strong>.</p>
            <div className="w-layout-blockcontainer content-container w-container">
              <div className="w-layout-grid pricing-grid" style={{ gridTemplateColumns: '1fr 1fr', maxWidth: '700px', margin: '0 auto' }}>
                <div className="pricing-card">
                  <div className="pricing-plan">Monthly Subscription</div>
                  <div className="price">$1.99/mo</div>
                  <p className="price-clarification">(First 90 days free)</p>
                  <ul role="list" className="list">
                    <li className="list-item">Unlimited stories</li>
                    <li className="list-item">50 GB of storage</li>
                  </ul>
                  <Link href="/app/register" className="btn w-button">Get Started</Link>
                </div>
                <div className="pricing-card">
                  <div className="pricing-plan">Single Story</div>
                  <div className="price">$99</div>
                  <p className="price-clarification">(One-time payment)</p>
                  <p className="price-clarification">5-year license — no recurring fees</p>
                  <ul role="list" className="list">
                    <li className="list-item">1 story</li>
                    <li className="list-item">50 GB of storage</li>
                  </ul>
                  <Link href="/app/register" className="btn w-button">Get Started</Link>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="footer">
              <img src="/webflow-home/images/appicon.png" loading="lazy" width="273.5" alt="Memvy App Icon" />
              <div className="header-container">
                <div className="descriptor-title">memvy is available now</div>
                <div className="y-padding-12"></div>
                <h2 className="h2 white">Start making memories, <span className="header-gradient">together.</span></h2>
                <div className="y-padding-12">
                  <Link data-w-id="8bd0c576-51ea-5f5d-c967-5bf7f08c9fea" href="/app/register" className="btn light w-button">Get Started Today</Link>
                </div>
                <div className="w-layout-grid social-icon-grid">
                  <a href="https://www.facebook.com/people/Memvyofficial/61560847231115/" target="_blank" className="w-inline-block">
                    <img src="/webflow-home/images/fb.svg" loading="lazy" alt="Facebook Logo" />
                  </a>
                  <a href="https://www.instagram.com/memvyofficial?igsh=MWp6bms1ZW41M3h0aQ==" target="_blank" className="w-inline-block">
                    <img src="/webflow-home/images/ig.svg" loading="lazy" alt="Instagram Logo" />
                  </a>
                  <a href="https://x.com/memvyofficial" target="_blank" className="w-inline-block">
                    <img src="/webflow-home/images/x_1.svg" loading="lazy" alt="Twitter Logo" />
                  </a>
                  <a href="https://www.tiktok.com/@memvy_official?_t=ZP-8vkVTvoSiIo&amp;_r=1" className="w-inline-block">
                    <img src="/webflow-home/images/tt.svg" loading="lazy" alt="TikTok Logo" />
                  </a>
                </div>
              </div>
              <div className="footer-fine-print">
                <div className="footer-left">
                  <img src="/webflow-home/images/Logo_Footer.svg" loading="lazy" alt="Memvy Logo" className="logo-footer" />
                  <div className="caption">(C) 2026 Falcon 9324, LLC. All rights reserved.</div>
                </div>
                <div className="footer-links">
                  <a href="/docs/Memvy-End-User-License-Agreement-2026-09-16.pdf" target="_blank" className="footer-link">Terms &amp; Conditions</a>
                  <Link href="/privacy" className="footer-link">Privacy Policy</Link>
                  <a href="mailto:contact@memvy.com?subject=Inquiry%20about%20Memvy" className="footer-link">Contact</a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<any> = async () => ({
  props: {
    ...(await serverSideTranslations('en', ['common'])),
  },
});

export default LandingPage;
